import React, { useContext, useMemo } from 'react';
import { IBenefit, Benefit, CustomField, Contributor, EntityValidations, ISpecificEntityFormProps } from '../../types';
import { DateService, LookupService, ValidationService } from '../../services';
import styles from '../../styles/cr.module.scss';
import { CrDropdown } from '../cr/CrDropdown';
import { CrUserPicker } from '../cr/CrUserPicker';
import { CrNumberTextField } from '../cr/CrNumberTextField';
import { EntityStatus } from '../../refData/EntityStatus';
import { CrTextField } from '../cr/CrTextField';
import { CrMultiDropdownWithText } from '../cr/CrMultiDropdownWithText';
import { CrReportingCyclePicker } from '../cr/CrReportingCyclePicker';
import { CrComboBox } from '../cr/CrComboBox';
import { EntityForm } from '../EntityForm';
import { CrDatePicker } from '../cr/CrDatePicker';
import { ReportingCycleService } from '../../services/ReportingCycleService';
import { DataContext } from '../DataContext';
import { CrCustomFields } from '../cr/CrCustomFields';
import { AppIDs, getFilteredCustomFieldDfns } from '../../AppGlobals';

export class BenefitValidations extends EntityValidations {
    public ProjectID: string = null;
    public TargetPerformanceLowerLimit: string = null;
    public TargetPerformanceUpperLimit: string = null;
    public NormalContributors: string = null;
    public ReportingCycle: string = null;
}

export const BenefitForm = (props: ISpecificEntityFormProps): React.ReactElement => {
    const { dataServices: { customFieldService, benefitService, contributorService } } = useContext(DataContext);
    const { lookupData, loadLookupData: { customFieldDfns, benefitTypes, entityStatuses, measurementUnits, projects, userProjects, users: { all: allUsers } } } = useContext(DataContext);

    useMemo(() => customFieldDfns(), [customFieldDfns]);
    useMemo(() => benefitTypes(), [benefitTypes]);
    useMemo(() => entityStatuses(), [entityStatuses]);
    useMemo(() => measurementUnits(), [measurementUnits]);
    useMemo(() => projects(), [projects]);
    useMemo(() => userProjects(), [userProjects]);
    useMemo(() => allUsers(), [allUsers]);

    const validateEntity = (benefit: IBenefit): Promise<BenefitValidations> => {
        const errors = new BenefitValidations();

        if (benefit.Title === null || benefit.Title === '') {
            errors.Title = 'Benefit name is required';
            errors.Valid = false;
        }
        else
            errors.Title = null;

        if (benefit.ProjectID === null) {
            errors.ProjectID = 'Project is required';
            errors.Valid = false;
        }
        else
            errors.ProjectID = null;

        if (benefit.TargetPerformanceLowerLimit !== null && isNaN(Number(benefit.TargetPerformanceLowerLimit))) {
            errors.TargetPerformanceLowerLimit = 'Target performance lower limit must be a number';
            errors.Valid = false;
        } else if (benefit.TargetPerformanceLowerLimit !== null && !ValidationService.validSqlDecimal(Number(benefit.TargetPerformanceLowerLimit))) {
            errors.TargetPerformanceLowerLimit = 'Target performance lower limit is too big';
            errors.Valid = false;
        }
        else
            errors.TargetPerformanceLowerLimit = null;

        if (benefit.TargetPerformanceUpperLimit !== null && isNaN(Number(benefit.TargetPerformanceUpperLimit))) {
            errors.TargetPerformanceUpperLimit = 'Target performance upper limit must be a number';
            errors.Valid = false;
        } else if (benefit.TargetPerformanceUpperLimit !== null && !ValidationService.validSqlDecimal(Number(benefit.TargetPerformanceUpperLimit))) {
            errors.TargetPerformanceUpperLimit = 'Target performance upper limit is too big';
            errors.Valid = false;
        }
        else
            errors.TargetPerformanceUpperLimit = null;

        if (benefit.Contributors.some(ct => ct.ContributorUserID == benefit.LeadUserID)) {
            errors.NormalContributors = 'A user cannot be both the lead and a contributor';
            errors.Valid = false;
        }
        else
            errors.NormalContributors = null;

        // if (!ReportingCycleService.reportingCycleIsValid(benefit)) {
        //     errors.ReportingCycle = 'Please select all values for the reporting cycle';
        //     errors.Valid = false;
        // }
        // else
        //     errors.ReportingCycle = null;

        return Promise.resolve(errors);
    };

    const filteredCustomFieldDfns = useMemo(() => {
        return getFilteredCustomFieldDfns(
            lookupData.CustomFieldDfns,
            [
                'All Apps - Common',
                'Project Portfolios - Benefits',
            ]
        );
    }, [lookupData.CustomFieldDfns]);


    return (
        <EntityForm<IBenefit, BenefitValidations>
            {...props}
            entityName="Benefit"
            renderFormFields={(changeHandlers, formState) => {
                const { FormData: benefit, ValidationErrors: errors } = formState;
                const mappedUsers = LookupService.projectUsers(lookupData, benefit.ProjectID, benefit);
                const suggestions = {
                    initialSuggestionsHeaderText: `Project users`,
                    initialSuggestions: mappedUsers,
                    noResultsFoundText: mappedUsers.length === 0 ? `No users are mapped to this benefit's project` : null
                };
                return (
                    <div>
                        {/* <CrComboBox
                            label="Project"
                            autoComplete="on"
                            className={styles.formField}
                            required={true}
                            options={LookupService.entitiesToSelectableOptions(lookupData.Projects)}
                            selectedKey={benefit.ProjectID}
                            onChange={(_, v) => changeHandlers.changeComboBox(v, 'ProjectID')}
                            errorMessage={errors.ProjectID}
                        /> */}
                        <CrTextField
                            label="Name"
                            className={styles.formField}
                            required={true}
                            maxLength={255}
                            value={benefit.Title}
                            onChange={(_, v) => changeHandlers.changeTextField(v, 'Title')}
                            errorMessage={errors.Title}
                        />
                        <CrTextField
                            label="Description"
                            className={styles.formField}
                            value={benefit.Description}
                            maxLength={500}
                            onChange={(_, v) => changeHandlers.changeTextField(v, 'Description')}
                            multiline
                        />
                        <CrDatePicker
                            label="Baseline realisation date"
                            className={styles.formField}
                            value={DateService.removeTimezoneOffset(benefit.BaselineDate)}
                            onSelectDate={d => changeHandlers.changeDatePicker(d, 'BaselineDate')}
                        />
                        {/* <CrReportingCyclePicker
                            label="Reports due:"
                            required={true}
                            className={styles.formField}
                            cycle={{ frequency: benefit.ReportingFrequency, dueDay: benefit.ReportingDueDay, startDate: benefit.ReportingStartDate }}
                            onChange={cycle => changeHandlers.changeReportingCycle(cycle)}
                            errorMessage={errors.ReportingCycle}
                        /> */}
                        <CrDropdown
                            label="Type"
                            className={styles.formField}
                            options={LookupService.entitiesToSelectableOptions(lookupData.BenefitTypes)}
                            selectedKey={benefit.BenefitTypeID}
                            onChange={(_, v) => changeHandlers.changeDropdown(v, 'BenefitTypeID')}
                        />
                        <CrDropdown
                            label="Target performance unit"
                            className={styles.formField}
                            options={LookupService.entitiesToSelectableOptions(lookupData.MeasurementUnits)}
                            selectedKey={benefit.MeasurementUnitID}
                            onChange={(_, v) => changeHandlers.changeDropdown(v, 'MeasurementUnitID')}
                        />
                        <CrNumberTextField
                            label="Target performance lower limit"
                            className={styles.formField}
                            maxLength={19}
                            value={benefit.TargetPerformanceLowerLimit}
                            onChange={(_, v) => changeHandlers.changeTextField(v, 'TargetPerformanceLowerLimit')}
                            errorMessage={errors.TargetPerformanceLowerLimit}
                        />
                        <CrNumberTextField
                            label="Target performance upper limit"
                            className={styles.formField}
                            maxLength={19}
                            value={benefit.TargetPerformanceUpperLimit}
                            onChange={(_, v) => changeHandlers.changeTextField(v, 'TargetPerformanceUpperLimit')}
                            errorMessage={errors.TargetPerformanceUpperLimit}
                        />
                        <CrUserPicker
                            label="Lead (L)"
                            className={styles.formField}
                            disabled={!benefit.ProjectID}
                            users={mappedUsers}
                            selectedUsers={benefit.LeadUserID && [benefit.LeadUserID]}
                            onChange={v => changeHandlers.changeUserPicker(v, 'LeadUserID')}
                            {...suggestions}
                        />
                        <CrUserPicker
                            label="Contributors (C)"
                            className={styles.formField}
                            disabled={!benefit.ProjectID}
                            users={mappedUsers}
                            itemLimit={3}
                            selectedUsers={benefit.Contributors?.map(c => c.ContributorUserID)}
                            onChange={v => changeHandlers.changeMultiUserPicker(v, 'Contributors', new Contributor(), 'ContributorUserID')}
                            errorMessage={errors.NormalContributors}
                            {...suggestions}
                        />

                        <CrCustomFields
                            label="Additional Information"
                            className={styles.formField}
                            customFieldDfns={filteredCustomFieldDfns}
                            selectedItems={LookupService.customFieldsToDropdownWithText(benefit.CustomFields)}
                            onChange={v => changeHandlers.changeMultiDropdownWithText(v, 'CustomFields', new CustomField(), 'CustomFieldDefnID', 'CustomFieldValue')}
                        />

                        {formState.FormDataBeforeChanges.EntityStatusID === EntityStatus.Closed &&
                            <CrDropdown
                                label="Status"
                                className={styles.formField}
                                options={LookupService.entitiesToSelectableOptions(lookupData.EntityStatuses)}
                                selectedKey={benefit.EntityStatusID}
                                onChange={(_, o) => changeHandlers.changeDropdown(o, 'EntityStatusID')}
                            />
                        }
                    </div>
                );
            }}
            loadEntity={benefitId => benefitService.read(benefitId, true, true)}
            loadNewEntity={() => new Benefit()}
            loadEntityValidations={() => new BenefitValidations()}
            onValidateEntity={validateEntity}
            onBeforeSave={benefit => {
                benefit.TargetPerformanceLowerLimit = benefit.TargetPerformanceLowerLimit || benefit.TargetPerformanceLowerLimit === 0 ? Number(benefit.TargetPerformanceLowerLimit) : null;
                benefit.TargetPerformanceUpperLimit = benefit.TargetPerformanceUpperLimit || benefit.TargetPerformanceUpperLimit === 0 ? Number(benefit.TargetPerformanceUpperLimit) : null;
            }}
            onCreate={b => benefitService.create(b)}
            onUpdate={b => benefitService.update(b.ID, b)}
            parentEntities={benefitService.parentEntities}
            childEntities={[
                { ObjectParentProperty: 'Contributors', ParentIdProperty: 'BenefitID', ChildIdProperty: 'ContributorUserID', ChildService: contributorService },
                { ObjectParentProperty: 'CustomFields', ParentIdProperty: 'BenefitID', ChildIdProperty: 'CustomFieldDefnID', ChildService: customFieldService }
            ]}
        />
    );
};
